import { CarrierPaymentMethod } from '@zorro/clients';
import { $enum } from 'ts-enum-util';

import { PaymentHandlerRole } from '../PaymentStatus/PaymentStatusUtils';
import { PaymentMethodToDisplay } from '../ZorroPay/paymentMethodsToDisplay.utils';

export enum PaymentMethodViewer {
  OPS = 'OPS',
  EMPLOYEE = 'EMPLOYEE',
  EMPLOYER_ADMIN = 'EMPLOYER_ADMIN',
}

export type MethodDisplaySpec = {
  method: PaymentMethodToDisplay;
  /**
   * Display policy only — the component still requires the card to actually
   * be active before showing the reveal button / who-pays note.
   */
  canReveal: boolean;
  showWhoPaysNote: boolean;
};

type GetMethodDisplaySpecsParams = {
  viewer: PaymentMethodViewer;
  carrierPaymentMethod: CarrierPaymentMethod;
  handlerRole: PaymentHandlerRole;
  isImpersonated: boolean;
};

/**
 * Ops sees all three methods unconditionally and may reveal only its own
 * card — unaffected by impersonation, since it's not the employee's
 * protected data. No who-pays note: that's exactly the signal ops should
 * ignore.
 */
const OPS_METHOD_SPECS: MethodDisplaySpec[] = [
  {
    method: PaymentMethodToDisplay.ACH,
    canReveal: false,
    showWhoPaysNote: false,
  },
  {
    method: PaymentMethodToDisplay.OPS_CARD,
    canReveal: true,
    showWhoPaysNote: false,
  },
  {
    method: PaymentMethodToDisplay.EMPLOYEE_CARD,
    canReveal: false,
    showWhoPaysNote: false,
  },
];

const EMPLOYEE_ACH_SPECS: MethodDisplaySpec[] = [
  {
    method: PaymentMethodToDisplay.ACH,
    canReveal: false,
    showWhoPaysNote: false,
  },
];

/**
 * Card follows who handles premium/auto-pay (SPLIT shows only the
 * employee's card). canRevealOwnCard is the caller's call — it already
 * knows which viewer it is by the time it gets here.
 */
function getCardSpecsByRole(
  handlerRole: PaymentHandlerRole,
  canRevealOwnCard: boolean
): MethodDisplaySpec[] {
  return $enum.mapValue(handlerRole).with<MethodDisplaySpec[]>({
    [PaymentHandlerRole.EMPLOYEE]: [
      {
        method: PaymentMethodToDisplay.EMPLOYEE_CARD,
        canReveal: canRevealOwnCard,
        showWhoPaysNote: true,
      },
    ],
    [PaymentHandlerRole.SPLIT]: [
      {
        method: PaymentMethodToDisplay.EMPLOYEE_CARD,
        canReveal: canRevealOwnCard,
        showWhoPaysNote: true,
      },
    ],
    [PaymentHandlerRole.AGENT]: [
      {
        method: PaymentMethodToDisplay.OPS_CARD,
        canReveal: false,
        showWhoPaysNote: true,
      },
    ],
  });
}

export function getMethodDisplaySpecs({
  viewer,
  carrierPaymentMethod,
  handlerRole,
  isImpersonated,
}: GetMethodDisplaySpecsParams): MethodDisplaySpec[] {
  const canRevealOwnCard = !isImpersonated;

  return $enum.mapValue(viewer).with<MethodDisplaySpec[]>({
    [PaymentMethodViewer.OPS]: OPS_METHOD_SPECS,
    [PaymentMethodViewer.EMPLOYEE]: $enum
      .mapValue(carrierPaymentMethod)
      .with<MethodDisplaySpec[]>({
        [CarrierPaymentMethod.ACH]: EMPLOYEE_ACH_SPECS,
        [CarrierPaymentMethod.CARD]: getCardSpecsByRole(
          handlerRole,
          canRevealOwnCard
        ),
      }),
    [PaymentMethodViewer.EMPLOYER_ADMIN]: $enum
      .mapValue(carrierPaymentMethod)
      .with<MethodDisplaySpec[]>({
        [CarrierPaymentMethod.ACH]: EMPLOYEE_ACH_SPECS,
        [CarrierPaymentMethod.CARD]: getCardSpecsByRole(handlerRole, false),
      }),
  });
}
